import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Dialog, DialogActions, DialogTitle, Button,
    Typography, Container, TextField, CircularProgress, Box,
    MenuItem, Select, InputLabel, FormControl
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { HBox, VBox } from '../../../assets/SharedComponents';
import { validateCouponAPI, validateRamatGanVoucherAPI } from '../../../api/api';
import { hasEligibleMembershipProducts } from '../../../utils/cartValidationUtils';

const RAMATGAN_ERROR_MESSAGES = {
    '400': 'couponInvalidRequest',
    '401': 'couponAuthError',
    '403': 'couponSupplierInactive',
    '600': 'couponExpired',
    '700': 'couponWrongId',
    '800': 'couponWrongSupplier',
    '900': 'couponAlreadyRedeemed',
    '500': 'errorOccuredText',
};

const getRamatGanErrorKey = (err) => {
    const code = err?.response?.data?.code;
    return RAMATGAN_ERROR_MESSAGES[String(code)] || 'errorOccuredText';
};

const calculateDiscount = (coupon, finalPrice, couponType) => {
    if (couponType === 'hever') return finalPrice;
    if (couponType === 'ramatgan') {
        const totalSubsidy = (coupon.activities || []).reduce(
            (sum, activity) => sum + (parseFloat(activity.subsidy_cost) || 0), 0
        );
        return Math.min(finalPrice, totalSubsidy);
    }
    const value = parseFloat(coupon.CouponValue) || 0;
    if (coupon.CouponDiscountType === 'PERCENTAGE') {
        return Math.min(finalPrice, Math.round(finalPrice * value / 100 * 100) / 100);
    }
    return Math.min(finalPrice, value);
};

const useStyles = makeStyles(() => ({
    dialog: {
        borderRadius: '25px !important',
        alignItems: 'center !important',
        justifyContent: 'space-around !important',
    },
    greenButton: {
        "&:hover": { background: '#2d5e29 !important' },
        "&.Mui-disabled": { backgroundColor: '#ccc !important', color: '#fff !important', cursor: 'not-allowed !important', pointerEvents: 'auto !important' },
        "&.Mui-disabled:hover": { background: '#ccc !important' },
        alignItems: 'center !important',
        justifyContent: 'center !important',
        marginBottom: '1.1rem !important',
        borderRadius: '50px !important',
        width: '14rem !important',
        backgroundColor: '#3a7735 !important',
        color: '#ffffff !important',
        fontSize: '1rem !important',
        textTransform: 'capitalize !important',
    },
    outlineButton: {
        alignItems: 'center !important',
        justifyContent: 'center !important',
        backgroundColor: '#ffffff !important',
        marginBottom: '1.1rem !important',
        border: '1px solid #3a7735 !important',
        borderRadius: '50px !important',
        width: '14rem !important',
        color: '#3a7735 !important',
        fontSize: '1rem !important',
        textTransform: 'capitalize !important',
        '&:hover': { backgroundColor: '#e8f5e9 !important' },
    },
    successBox: {
        backgroundColor: '#e8f5e9',
        borderRadius: '12px',
        padding: '0.75rem 1.5rem',
        margin: '0.5rem 0',
        width: '100%',
    },
    warningBox: {
        backgroundColor: '#fff3cd',
        borderRadius: '12px',
        padding: '0.75rem 1.5rem',
        margin: '0.5rem 0',
        width: '100%',
        maxHeight: '150px',
        overflowY: 'auto',
    },
    removedProductsList: {
        fontSize: '0.85rem',
        color: '#666',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
}));

const CouponDialog = ({ open, setOpen, finalPrice, onCouponApplied, couponType, products, household, onProductsRemoved, alreadyAppliedChildIds = [], onGoToPayment }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [barcode, setBarcode] = useState('');
    const [couponId, setCouponId] = useState('');
    const [idNumber, setIdNumber] = useState('');
    const [selectedChildId, setSelectedChildId] = useState('');
    const [loading, setLoading] = useState(false);
    const [validatedCoupon, setValidatedCoupon] = useState(null);
    const [appliedSuccess, setAppliedSuccess] = useState(false);

    // Unique children not yet assigned a coupon
    const uniqueChildren = Array.from(
        new Map((products || []).map(p => [p.LicTradNum, p])).values()
    ).filter(c => !alreadyAppliedChildIds.includes(String(c.LicTradNum)));

    const handleClose = () => {
        setOpen(false);
        resetForm();
    };

    const resetForm = () => {
        setBarcode('');
        setCouponId('');
        setIdNumber('');
        setSelectedChildId('');
        setValidatedCoupon(null);
        setAppliedSuccess(false);
    };

    const handleValidate = async () => {
        if (couponType === 'hever') {
            await validateHeverCoupon();
        } else if (couponType === 'ramatgan') {
            await validateRamatGanCoupon();
        }
    };

    const validateHeverCoupon = async () => {
        if (!barcode.trim()) return;
        setLoading(true);
        setValidatedCoupon(null);
        try {
            const result = await validateCouponAPI(barcode.trim());
            setValidatedCoupon(result);
        } catch (err) {
            const msg = err?.message || t('errorOccuredText');
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const validateRamatGanCoupon = async () => {
        if (!couponId.trim() || !idNumber.trim()) {
            enqueueSnackbar(t('couponRequiredFields', 'חובה למלא את כל השדות'), { variant: 'warning' });
            return;
        }
        setLoading(true);
        setValidatedCoupon(null);
        try {
            const response = await validateRamatGanVoucherAPI({
                couponId: couponId.trim(),
                idNumber: idNumber.trim(),
            });
            const result = response.data;

            // API returns 200 with error code in body for failures
            if (result.code && result.code !== '200') {
                enqueueSnackbar(t(RAMATGAN_ERROR_MESSAGES[String(result.code)] || 'errorOccuredText'), { variant: 'error' });
                return;
            }

            // Validate coupon status
            if (result.couponStatus === 2) {
                enqueueSnackbar(t('couponAlreadyRedeemed', 'קופון זה כבר שימש'), { variant: 'error' });
                return;
            }
            if (result.couponStatus === 3) {
                enqueueSnackbar(t('couponCancelled', 'קופון זה בוטל'), { variant: 'error' });
                return;
            }
            if (result.couponStatus === 4) {
                enqueueSnackbar(t('couponExpired', 'קופון זה פג תוקף'), { variant: 'error' });
                return;
            }

            // Ramat Gan coupon applies to מיסי חבר only (ItmsGrpCod=160 or ItemPurposeType=4)
            if (!hasEligibleMembershipProducts(products || [], idNumber)) {
                enqueueSnackbar(t('couponNotMembershipFee', 'הקופון תקף למיסי חבר בלבד'), { variant: 'error' });
                return;
            }

            setValidatedCoupon(result);
        } catch (err) {
            enqueueSnackbar(t(getRamatGanErrorKey(err), 'שגיאה בבדיקת הקופון'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleApply = () => {
        if (!validatedCoupon) return;
        const discountAmount = calculateDiscount(validatedCoupon, finalPrice, couponType);
        onCouponApplied({ ...validatedCoupon, discountAmount, type: couponType, assignedChildId: idNumber, couponCode: couponId, couponAssignee: idNumber });
        setAppliedSuccess(true);
        setValidatedCoupon(null);
    };

    const handleAddAnother = () => {
        resetForm();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '25px !important',
                    backgroundColor: '#f0f3f0',
                }
            }}>
            <Container>
                <DialogTitle>
                    <HBox className={classes.dialog}>
                        <DialogActions>
                            <Button onClick={handleClose} sx={{ marginLeft: { xs: '10rem', md: '22rem' } }}>
                                <CloseOutlinedIcon />
                            </Button>
                        </DialogActions>
                    </HBox>
                </DialogTitle>

                <VBox alignItems="center" sx={{ pb: '1.5rem', px: '1rem', gap: '1rem' }}>
                    <LocalOfferOutlinedIcon sx={{ color: '#3a7735', fontSize: '2.5rem' }} />
                    {!appliedSuccess && (
                        <>
                            <Typography sx={{ color: '#3a7735', fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center' }}>
                                {t('hasCoupon')}
                            </Typography>
                            <Typography sx={{ color: '#555', textAlign: 'center', fontSize: '0.9rem' }}>
                                {couponType === 'hever'
                                    ? t('couponSubtitle')
                                    : t('couponRamatGanSubtitle', 'הזן קוד קופון ומספר זהות')}
                            </Typography>
                        </>
                    )}

                    {/* Applied success confirmation */}
                    {appliedSuccess && (
                        <Box className={classes.successBox}>
                            <HBox alignItems="center" gap="0.75rem">
                                <CheckCircleOutlineIcon sx={{ color: '#3a7735', flexShrink: 0 }} />
                                <Typography sx={{ color: '#3a7735', fontWeight: 'bold' }}>
                                    {t('couponAppliedSuccess', 'הקופון הוחל בהצלחה!')}
                                </Typography>
                            </HBox>
                        </Box>
                    )}

                    {/* Hever Input */}
                    {!appliedSuccess && couponType === 'hever' && (
                        <HBox alignItems="center" gap="0.5rem" sx={{ width: '100%', justifyContent: 'center' }}>
                            <TextField
                                variant="outlined"
                                size="small"
                                placeholder={t('couponPlaceholder')}
                                value={barcode}
                                onChange={e => {
                                    setBarcode(e.target.value);
                                    setValidatedCoupon(null);
                                }}
                                onKeyDown={e => e.key === 'Enter' && handleValidate()}
                                inputProps={{ style: { textAlign: 'center', letterSpacing: '0.05rem' } }}
                                sx={{
                                    width: '12rem',
                                    '& .MuiOutlinedInput-root': { borderRadius: '50px' },
                                }}
                            />
                            <Button
                                onClick={handleValidate}
                                disabled={loading || !barcode.trim()}
                                sx={{
                                    borderRadius: '50px',
                                    backgroundColor: '#3a7735',
                                    color: '#fff',
                                    px: 2,
                                    '&:hover': { backgroundColor: '#2d5e29' },
                                    '&:disabled': { backgroundColor: '#ccc' },
                                }}>
                                {loading
                                    ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                                    : (t('couponCheck'))}
                            </Button>
                        </HBox>
                    )}

                    {/* Ramat Gan Inputs */}
                    {!appliedSuccess && couponType === 'ramatgan' && (
                        <VBox gap="0.75rem" sx={{ width: '100%' }}>

                            {/* Child selector */}
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('couponSelectChild', 'בחר חניך')}</InputLabel>
                                <Select
                                    value={selectedChildId}
                                    label={t('couponSelectChild', 'בחר חניך')}
                                    onChange={e => {
                                        const child = uniqueChildren.find(c => c.LicTradNum === e.target.value);
                                        setSelectedChildId(e.target.value);
                                        setIdNumber(child ? child.LicTradNum : '');
                                        setValidatedCoupon(null);
                                    }}
                                    sx={{ borderRadius: '10px' }}
                                >
                                    {uniqueChildren.map(child => (
                                        <MenuItem key={child.LicTradNum} value={child.LicTradNum}>
                                            {`${child.Pupil_FirstName} ${child.Pupil_lastName || ''} - ${child.LicTradNum}`.trim()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                variant="outlined"
                                size="small"
                                label={t('couponRamatGanID', 'קוד קופון')}
                                placeholder={t('couponRamatGanID', 'קוד קופון')}
                                value={couponId}
                                onChange={e => {
                                    setCouponId(e.target.value);
                                    setValidatedCoupon(null);
                                }}
                                fullWidth
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                            <Button
                                onClick={handleValidate}
                                disabled={loading || !couponId.trim() || !selectedChildId}
                                fullWidth
                                sx={{
                                    borderRadius: '50px',
                                    backgroundColor: '#3a7735',
                                    color: '#fff',
                                    py: 1,
                                    '&:hover': { backgroundColor: '#2d5e29' },
                                    '&:disabled': { backgroundColor: '#ccc' },
                                }}>
                                {loading
                                    ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                                    : (t('couponCheck'))}
                            </Button>
                        </VBox>
                    )}

                    {/* Success Message */}
                    {validatedCoupon && (
                        <Box className={classes.successBox}>
                            <HBox alignItems="center" gap="0.75rem">
                                <CheckCircleOutlineIcon sx={{ color: '#3a7735', flexShrink: 0 }} />
                                <VBox>
                                    <Typography sx={{ color: '#3a7735', fontWeight: 'bold' }}>
                                        {t('couponValid')}
                                    </Typography>
                                    {validatedCoupon.title && (
                                        <Typography sx={{ color: '#3a7735', fontSize: '0.9rem' }}>
                                            {validatedCoupon.title}
                                        </Typography>
                                    )}
                                    {validatedCoupon.couponDesc && (
                                        <Typography sx={{ color: '#3a7735', fontSize: '0.9rem' }}>
                                            {validatedCoupon.couponDesc}
                                        </Typography>
                                    )}
                                    <Typography sx={{ color: '#3a7735', fontSize: '0.9rem', mt: '0.25rem' }}>
                                        {t('couponNewTotal')}{': '}
                                        <strong>₪{Math.max(0, finalPrice - calculateDiscount(validatedCoupon, finalPrice, couponType)).toFixed(2)}</strong>
                                    </Typography>
                                </VBox>
                            </HBox>
                        </Box>
                    )}

                    <DialogActions sx={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'center', width: '100%' }}>
                        {appliedSuccess ? (
                            <>
                                {uniqueChildren.length > 0 && (
                                    <Button className={classes.greenButton} onClick={handleAddAnother} fullWidth>
                                        {t('addAnotherCoupon')}
                                    </Button>
                                )}
                                <Button className={classes.greenButton} onClick={() => { handleClose(); onGoToPayment?.(); }} fullWidth>
                                    {t('couponGoToPayment')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    className={classes.greenButton}
                                    onClick={handleApply}
                                    fullWidth
                                    disabled={!validatedCoupon}>
                                    {t('couponApply')}
                                </Button>
                                <Button className={classes.outlineButton} onClick={handleClose} fullWidth>
                                    {t('cancel')}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </VBox>
            </Container>
        </Dialog>
    );
};

export default CouponDialog;
